// Third-party
import moment from 'moment';
import get from 'lodash/get';

// Utils
import { PERIOD_MONTHS, TIME_PERIODS, ASSET_TYPE, YieldParamsType, BOND_TAG, HOLDING_NAME_MAX_LENGTH } from '../constants';
import { splitPortfolioHoldings, PortfolioData, HOLDING_TYPES, getPortfolioHoldingData } from '../portfolios';
import { uniqueArray } from '../objects';
import { getLastNYears, getDateFromTimePeriod } from '../time';
import { formatMessage, formatLocalizedString, LOCALE, formatMessageWithLocale } from '../i18n';
import PortfolioHolding from '@/types/portfolio-holding';
import ComparisonHolding from '@/types/comparison-holding';
import Holding from '@/types/holding';
import Stock from '@/types/stock';
import Fund from '@/types/fund';
import Benchmark from '@/types/benchmark';
import Portfolio from '@/types/portfolio';
import Nullable from '@/types/nullable';
import RollingPerformance from '@/types/rolling-performance';
import ShortHistoryDisclaimerHolding from '@/types/short-history-disclaimer-holding';
import CustomDisclaimerHolding from '@/types/custom-disclaimer-holding';
import ComparisonItem from '@/types/comparison-item';
import ComparisonBenchmark from '@/types/comparison-benchmark';
import BenchmarkProviderData from '@/types/benchmark-provider-data';
import Locale from '@/types/locale';
import TimePeriods from '@/types/time-periods';
import ShortHistoryDisclaimer from '@/types/short-history-disclaimer';
import CommonInception from '@/types/common-inception';
import Alt from '@/types/models/alt';

const formatComparisonItemHoldingsList = (holdings: ComparisonHolding[]) =>
  holdings.map(holding => {
    if (!holding.rolling || !holding.rolling.monthly.length) {
      return holding.symbol;
    }

    return `${holding.symbol} (${moment(holding.rolling.monthly[0][0]).format(formatMessage('Common.DateFormats.Short'))})`;
  });

const formatPortfolioHoldingsList = (holdings: PortfolioHolding[], data: PortfolioData) =>
  holdings.map(h => {
    const holding = getPortfolioHoldingData(h, data);

    if (!holding || !holding.performance.rolling.monthly.length) {
      return h.symbol;
    }

    return `${h.symbol} (${moment(holding.performance.rolling.monthly[0][0]).format(formatMessage('Common.DateFormats.Short'))})`;
  });

/**
 * Evaluates if a holding (fund or stock) has data or not.
 *
 * @param holding Holding to be evaluated.
 * @returns `true` if holding has rolling data; `false` otherwise.
 */
const holdingHasNoData = (holding: Holding) => {
  const { rolling } = holding.performance;
  return !rolling || !rolling.monthly || !rolling.monthly.length;
};

/**
 * Evaluates if a holding (fund or stock) has no nav data or not.
 *
 * @param holding Holding to be evaluated.
 * @returns `true` if holding has nav data; `false` otherwise.
 */
const holdingHasNoNavData = (holding: Stock | Fund) => {
  const { nav } = holding;
  return !nav || !nav.monthly || !nav.monthly.length;
};

/**
 * Evaluates if a holding is a GIC or not
 *
 * @param holding Holding to be evaluated.
 * @returns `true` if holding is a gic; `false` otherwise.
 */
const holdingIsGIC = (holding: Holding) => {
  const holdingAsFund = holding as Fund;
  return holdingAsFund.customYield && holdingAsFund.customYield.type === YieldParamsType.GIC;
};

/**
 * Evaluates if a holding is a GIC or not
 *
 * @param holding Holding to be evaluated.
 * @returns `true` if holding is a HISA; `false` otherwise.
 */
const holdingIsHISA = (holding: Holding) => {
  const holdingAsFund = holding as Fund;
  return holdingAsFund.customYield && holdingAsFund.customYield.type === YieldParamsType.HISA;
};

/**
 * Evaluates if a holding (fund or stock) is closed (no data in over a month).
 *
 * @param holding Holding to be evaluated.
 * @returns `true` if holding has no new data in the last month; `false` otherwise.
 */
const holdingIsClosed = (holding: Holding) => {
  const { rolling } = holding.performance;

  if (!rolling.monthly || !rolling.monthly.length) {
    return false;
  }

  const lastTimestamp = rolling.monthly[rolling.monthly.length - 1][0];
  const trigger = moment().subtract(2, 'month').startOf('month').toDate().getTime();

  return lastTimestamp < trigger;
};

/**
 * Evaluates if a holding (fund or stock) is missing underlying data.
 *
 * @param holding Holding to be evaluated.
 * @returns `true` if holding is missing; `false` otherwise.
 */
const holdingIsMissing = (holding: PortfolioHolding) => holding.original.id == null;

/**
 * Evaluates if a holding (fund or stock) is closed (no nav data in over a month).
 *
 * @param holding Holding to be evaluated.
 * @returns `true` if holding has no new nav data in the last month; `false` otherwise.
 */
const holdingNavIsClosed = (holding: Stock | Fund) => {
  const { nav } = holding;

  if (!nav || !nav.monthly || !nav.monthly.length) {
    return false;
  }

  const lastTimestamp = nav.monthly[nav.monthly.length - 1][0];
  const trigger = moment().subtract(1, 'month').startOf('month').toDate().getTime();

  return lastTimestamp < trigger;
};

/**
 * Gets the date of the first monthly data point for the specified holding (fund or stock).
 *
 * @param holding Holding to be evaluated.
 * @returns {number} Timestamp of the first data point.
 */
const getFirstDataPointDate = (holding: Holding) => {
  const { rolling } = holding.performance;
  if (!rolling.monthly || !rolling.monthly.length) {
    return '';
  }

  return rolling.monthly[0][0];
};

/**
 * Gets the date of the last monthly data point for the specified holding (fund or stock).
 *
 * @param holding Holding to be evaluated.
 * @returns {number} Timestamp of the last data point.
 */
const getLastDataPointDate = (holding: Holding) => {
  const { rolling } = holding.performance;
  if (!rolling.monthly || !rolling.monthly.length) {
    return '';
  }

  return rolling.monthly[rolling.monthly.length - 1][0];
};

/**
 * Gets the date of the last monthly nav data point for the specified holding (fund or stock).
 *
 * @param holding Holding to be evaluated.
 * @returns {number} Timestamp of the last data point.
 */
const getLastNavDataPointDate = (holding: Stock | Fund) => {
  const { nav } = holding;
  if (!nav || !nav.monthly || !nav.monthly.length) {
    return '';
  }

  return nav.monthly[nav.monthly.length - 1][0];
};

/**
 * Evaluates if a holding (fund or stock) has less than 3 years of data.
 *
 * @param holding Holding to be evaluated.
 * @returns `true` if holding has less than 3 years of data; `false` otherwise.
 */
const holdingHasLessThan3YData = (holding: Holding) => {
  const { rolling } = holding.performance;
  return !rolling.monthly || rolling.monthly.length < PERIOD_MONTHS['3Y'];
};

/**
 * Evaluates if a holding (fund or stock) has less than 1 year of data.
 *
 * @param holding Holding to be evaluated.
 * @returns `true` if holding has less than 1 year of data; `false` otherwise.
 */
const holdingHasLessThan1YData = (holding: Holding) => {
  const { rolling } = holding.performance;
  return !rolling.monthly || rolling.monthly.length < PERIOD_MONTHS['1Y'];
};

/**
 * Generic helper to check if a numeric value is zero or null.
 *
 * @param value The numeric value to check.
 * @returns `true` if value is 0 or null; `false` otherwise.
 */
const isZeroOrNull = (value: number | null | undefined): boolean => value == null || value === 0;

/**
 * Evaluates if a holding has units that are 0 or null.
 *
 * @param holding PortfolioHolding to be evaluated.
 * @returns `true` if holding units are 0 or null; `false` otherwise.
 */
const holdingUnitsIsZeroOrNull = (holding: PortfolioHolding): boolean => isZeroOrNull(holding.units);

/**
 * Evaluates if a holding has weight that is 0 or null.
 *
 * @param holding PortfolioHolding to be evaluated.
 * @returns `true` if holding weight is 0 or null; `false` otherwise.
 */
const holdingWeightIsZeroOrNull = (holding: PortfolioHolding): boolean => isZeroOrNull(holding.weight);

/**
 * Evaluates if a holding has dollars that are 0 or null.
 *
 * @param holding PortfolioHolding to be evaluated.
 * @returns `true` if holding dollars are 0 or null; `false` otherwise.
 */
const holdingDollarsIsZeroOrNull = (holding: PortfolioHolding): boolean => isZeroOrNull(holding.dollars);

/**
 * Generic helper to check if a numeric value is negative.
 *
 * @param value The numeric value to check.
 * @returns `true` if value is negative; `false` otherwise.
 */
const isNegative = (value: number | null | undefined): boolean => value != null && value < 0;

/**
 * Evaluates if a holding has negative units.
 *
 * @param holding PortfolioHolding to be evaluated.
 * @returns `true` if holding units are negative; `false` otherwise.
 */
const holdingUnitsIsNegative = (holding: PortfolioHolding): boolean => isNegative(holding.units);

/**
 * Evaluates if a holding has negative weight.
 *
 * @param holding PortfolioHolding to be evaluated.
 * @returns `true` if holding weight is negative; `false` otherwise.
 */
const holdingWeightIsNegative = (holding: PortfolioHolding): boolean => isNegative(holding.weight);

/**
 * Evaluates if a holding has negative dollars.
 *
 * @param holding PortfolioHolding to be evaluated.
 * @returns `true` if holding dollars are negative; `false` otherwise.
 */
const holdingDollarsIsNegative = (holding: PortfolioHolding): boolean => isNegative(holding.dollars);

/**
 * Evaluates if a holding has any invalid values (zero, null, or negative) for units.
 *
 * @param holding PortfolioHolding to be evaluated.
 * @returns `true` if holding units are zero, null, or negative; `false` otherwise.
 */
const holdingUnitsIsInvalid = (holding: PortfolioHolding): boolean => holdingUnitsIsZeroOrNull(holding) || holdingUnitsIsNegative(holding);

/**
 * Evaluates if a holding has any invalid values (zero, null, or negative) for weight.
 *
 * @param holding PortfolioHolding to be evaluated.
 * @returns `true` if holding weight is zero, null, or negative; `false` otherwise.
 */
const holdingWeightIsInvalid = (holding: PortfolioHolding): boolean => holdingWeightIsZeroOrNull(holding) || holdingWeightIsNegative(holding);

/**
 * Evaluates if a holding has any invalid values (zero, null, or negative) for dollars.
 *
 * @param holding PortfolioHolding to be evaluated.
 * @returns `true` if holding dollars are zero, null, or negative; `false` otherwise.
 */
const holdingDollarsIsInvalid = (holding: PortfolioHolding): boolean => holdingDollarsIsZeroOrNull(holding) || holdingDollarsIsNegative(holding);

/**
 * Evaluates if a holding has any invalid financial values (units, weight, or dollars).
 *
 * @param holding PortfolioHolding to be evaluated.
 * @returns `true` if any of units, weight, or dollars are invalid; `false` otherwise.
 */
const holdingHasInvalidFinancialValues = (holding: PortfolioHolding): boolean => {
  if (!holding.external) {
    return false;
  }

  if (!holding.units && (holdingWeightIsInvalid(holding) || holdingDollarsIsInvalid(holding))) {
    return true;
  }

  if ((!holding.weight || !holding.dollars) && holdingUnitsIsInvalid(holding)) {
    return true;
  }

  return false;
};

/**
 * Evaluates if a holding is missing OR has invalid financial values.
 * Combines the logic of holdingIsMissing and holdingHasInvalidFinancialValues.
 *
 * @param holding PortfolioHolding to be evaluated.
 * @returns `true` if holding is missing OR has invalid financial values; `false` otherwise.
 */
const holdingIsMissingOrInvalid = (holding: PortfolioHolding): boolean => holdingIsMissing(holding) || holdingHasInvalidFinancialValues(holding);

/**
 * Evaluates if a portfolio has issues with missing holdings OR any invalid holding financial values.
 * This is the core validation function that checks for all possible holding issues.
 *
 * @param portfolio Portfolio to be evaluated.
 * @returns `true` if portfolio has ANY of the following issues: missing holdings, zero/null units, zero/null weight, zero/null dollars, negative units, negative weight, negative dollars; `false` otherwise.
 */
const portfolioHasMissingOrInvalidHoldings = (portfolio: Portfolio): boolean => portfolio.holdings.some(holdingIsMissingOrInvalid);

/**
 * Evaluates if a fund has all the benchmark data needed to evaluate the risk metrics.
 *
 * @param fund Fund to be evaluated.
 * @returns `true` if holding has all benchmark data needed; `false` otherwise.
 */
const fundHasAllBenchmarkData = (fund: Fund, benchmarks: Benchmark[]) => {
  if (fund._id.startsWith(BOND_TAG)) {
    return true;
  }

  if (!fund.benchmarkLipperId || !fund.riskFreeLipperId) {
    return false;
  }

  const benchmarkIds = uniqueArray([fund.benchmarkLipperId, fund.riskFreeLipperId]);
  const benchmarksData = benchmarks.filter(b => benchmarkIds.some(id => b.lipperId.toString() === id.toString()));
  if (benchmarksData.length !== benchmarkIds.length) {
    return false;
  }

  for (const benchmark of benchmarksData) {
    if (!benchmark.performance.rolling || !benchmark.performance.rolling.monthly || benchmark.performance.rolling.monthly.length < PERIOD_MONTHS['1Y']) {
      return false;
    }
  }

  return true;
};

/**
 * Evaluates if the  fund has the same benchmarks (risk free and market)
 * @param {Fund} fund the fund to be evaluated
 * @returns `true` if holding has the same benchmark data; `false` otherwise.
 */
const fundHasSameBenchmark = (fund: Fund) => {
  if (!fund.benchmarkLipperId || !fund.riskFreeLipperId) {
    return false;
  }
  return fund.riskFreeLipperId === fund.benchmarkLipperId;
};

const sumAllocations = (obj: any, path: string) => {
  const allocations = get(obj, path, []) as { weight: number }[];

  return allocations.reduce((total, allocation) => total + allocation.weight, 0);
};

const getAltDisclaimers = (alt: Alt) => {
  if (!alt.disclaimers) return [];

  return alt.disclaimers.map(disclaimer => {
    const disclaimerData = disclaimer.content && Object.values(disclaimer.content).some(value => value !== '');
    if (!disclaimerData) return [];

    return {
      name: formatLocalizedString(alt?.names?.full),
      ...disclaimer,
    };
  });
};

const getFundDisclaimers = (fund: Fund) => {
  if (!fund.disclaimers) {
    return [];
  }

  return fund.disclaimers.map(disclaimer => {
    const disclaimerData = disclaimer.content && Object.values(disclaimer.content).some(value => value !== '');
    if (!disclaimerData) {
      return [];
    }
    return {
      name: formatLocalizedString(fund.names?.full),
      symbol: fund.symbols && fund.symbols[0],
      ...disclaimer,
    };
  });
};

/**
 * Evaluates if a fund has all asset details data.
 *
 * @param fund Fund to be evaluated.
 * @returns `true` if the fund has all asset details data; `false` otherwise.
 */
const fundHasAllAssetDetailsData = (fund: Fund) => Math.round(sumAllocations(fund, 'fundPortfolio.allocations.asset')) >= 100;

/**
 * Evaluates if a fund has all fixed income style data.
 *
 * @param fund Fund to be evaluated.
 * @returns `true` if the fund has all fixed income style data; `false` otherwise.
 */
const fundHasAllFixedIncomeStyleData = (fund: Fund) => {
  const { investmentInfo } = fund.fundPortfolio;
  return investmentInfo && investmentInfo.durationClass && investmentInfo.creditRatingClass;
};

/**
 * Evaluates if a fund has all equity style data
 *
 * @param fund Fund to be evaluated.
 * @returns `true` if the fund has all equity style data; `false` otherwise.
 */
const fundHasAllEquityStyleData = (fund: Fund) => {
  const { investmentInfo } = fund.fundPortfolio;
  return investmentInfo && investmentInfo.capitalization && investmentInfo.style;
};

/**
 * Evaluates if a portfolio has all asset details data.
 *
 * @param portfolio Portfolio to be evaluated.
 * @returns `true` if the fund has all asset details data; `false` otherwise.
 */
const portfolioHasAllAssetDetailsData = (portfolio: Portfolio) => Math.round(sumAllocations(portfolio, 'allocations.asset')) >= 100;

/**
 * Evaluates if a fund has all top holdings data.
 *
 * @param fund Fund to be evaluated.
 * @returns `true` if the fund has all top holdings data; `false` otherwise.
 */
const fundHasAllTopHoldingsData = (fund: Fund) => Math.round(sumAllocations(fund, 'fundPortfolio.holdings.list')) >= 100;

/**
 * Evaluates if a portfolio has a missing holding.
 *
 * @param portfolio Portfolio to be evaluated.
 * @returns `false` if the portfolio has at least one missing holding; `true` otherwise.
 */
const portfolioHasNoMissingHolding = (portfolio: Portfolio): boolean => !portfolio.holdings.some(h => holdingIsMissing(h));

/**
 * Evaluates if a portfolio has all top holdings data.
 *
 * @param portfolio Portfolio to be evaluated.
 * @returns `true` if the portfolio has all top holdings data; `false` otherwise.
 */
const portfolioHasAllTopHoldingsData = (portfolio: Portfolio) => Math.round(sumAllocations(portfolio, 'detailedHoldings.list')) >= 100;

/**
 * Evaluates if a fund has all country allocation data.
 *
 * @param fund Fund to be evaluated.
 * @returns `true` if the fund has all country allocation data; `false` otherwise.
 */
const fundHasAllCountryAllocationData = (fund: Fund) => Math.round(sumAllocations(fund, 'fundPortfolio.allocations.country')) >= 100;

/**
 * Evaluates if a fund has all maturity allocation data.
 *
 * @param fund Fund to be evaluated.
 * @returns `true` if the fund has all maturity allocation data; `false` otherwise.
 */
const fundHasAllMaturityAllocationData = (fund: Fund) => Math.round(sumAllocations(fund, 'fundPortfolio.allocations.maturity')) >= 100;

/**
 * Evaluates if a fund has all credit quality allocation data.
 *
 * @param fund Fund to be evaluated.
 * @returns `true` if the fund has all credit quality allocation data; `false` otherwise.
 */
const fundHasAllCreditQualityAllocationData = (fund: Fund) => Math.round(sumAllocations(fund, 'fundPortfolio.allocations.creditQuality')) >= 100;

/**
 * Evaluates if a fund has all fixed income regions allocation data.
 *
 * @param fund Fund to be evaluated.
 * @returns `true` if the fund has all fixed income regions allocation data; `false` otherwise.
 */
const fundHasAllFixedIncomeRegionsAllocationData = (fund: Fund) => Math.round(sumAllocations(fund, 'fundPortfolio.allocations.fixedIncomeRegion')) >= 100;

/**
 * Evaluates if a fund has all fixed income sector allocation data.
 *
 * @param fund Fund to be evaluated.
 * @returns `true` if the fund has all fixed income sector allocation data; `false` otherwise.
 */
const fundHasAllFixedIncomeSectorAllocationData = (fund: Fund) => Math.round(sumAllocations(fund, 'fundPortfolio.allocations.fixedIncomeSector')) >= 100;

/**
 * Evaluates if a fund has all industry holdings data.
 *
 * @param fund Fund to be evaluated.
 * @returns `true` if the fund has all country allocation data; `false` otherwise.
 */
const fundHasAllIndustryHoldingsData = (fund: Fund) => Math.round(sumAllocations(fund, 'fundPortfolio.allocations.sector')) >= 100;

/**
 * Evaluates if a fund should have credit quality data. Evaluation based on the fixed income allocation of the fund.
 *
 * @param fund Fund to be evaluated.
 * @returns `true` if the fund should have credit quality data; `false` otherwise.
 */
const fundHasCreditSecurities = (fund: Fund) => {
  const allocations = fund.fundPortfolio && fund.fundPortfolio.allocations;

  return (
    allocations &&
    allocations.asset &&
    allocations.asset.some(a => {
      const nameEN = a.name[LOCALE.EN_CA];
      return !!nameEN && nameEN.toLowerCase().indexOf('fixed income') > -1 && a.weight > 0;
    })
  );
};

/**
 * Evaluates if a portfolio should have fixed income data.
 *
 * @param fund Portfolio to be evaluated.
 * @returns `true` if the portfolio should have fixed income data; `false` otherwise.
 */
const portfolioHasCreditSecurities = (portfolio: Portfolio) => {
  const { allocations } = portfolio;

  return (
    allocations &&
    allocations.asset &&
    allocations.asset.some(a => {
      const nameEN = a.name[LOCALE.EN_CA];
      return !!nameEN && nameEN.toLowerCase().indexOf('fixed income') > -1 && a.weight > 0;
    })
  );
};

/**
 * Evaluates if a fund should have equity data.
 *
 * @param fund Fund to be evaluated.
 * @returns `true` if the fund should have equity data; `false` otherwise.
 */
const fundHasEquity = (fund: Fund) => {
  const allocations = fund.fundPortfolio && fund.fundPortfolio.allocations;

  return (
    allocations &&
    allocations.asset &&
    allocations.asset.some(a => {
      const nameEN = a.name[LOCALE.EN_CA];
      return !!nameEN && nameEN.toLowerCase().indexOf('equity') > -1 && a.weight > 0;
    })
  );
};

/**
 * Trims and adds ellipsis to a holding name.
 *
 * @param name Name to be trimmed.
 * @returns the trimmed string.
 */
const getTrimmedHoldingName = (name: string) => (name.length >= HOLDING_NAME_MAX_LENGTH ? `${name.slice(0, HOLDING_NAME_MAX_LENGTH - 3)}...` : name);

/**
 * Evaluates if a portfolio should have equity data.
 *
 * @param fund Portfolio to be evaluated.
 * @returns `true` if the portfolio should have equity data; `false` otherwise.
 */
const portfolioHasEquity = (portfolio: Portfolio) => {
  const { allocations } = portfolio;

  return (
    allocations &&
    allocations.asset &&
    allocations.asset.some(a => {
      const nameEN = a.name[LOCALE.EN_CA];
      return !!nameEN && nameEN.toLowerCase().indexOf('equity') > -1 && a.weight > 0;
    })
  );
};

/**
 * Evaluates if a fund has all industry holdings data.
 *
 * @param fund Fund to be evaluated.
 * @returns `true` if the fund has all country allocation data; `false` otherwise.
 */
const portfolioHasAllIndustryHoldingsData = (portfolio: Portfolio, data: PortfolioData) => {
  const industryAllocation = sumAllocations(portfolio, 'allocations.sector');

  const portfolioBonds = portfolio.holdings.filter(h => {
    if (h.original.type !== HOLDING_TYPES.FUND) {
      return false;
    }

    const fundData = data.funds.find(f => f._id === h.original.id);
    return fundData && fundData.assetType && fundData.assetType[LOCALE.EN_CA] === ASSET_TYPE.BOND;
  });

  // Bonds do not have industry allocations - They should not define if all the data is available.
  const bondsAllocation = portfolioBonds.reduce((total, bond) => total + bond.weight, 0);

  return Math.round(industryAllocation + bondsAllocation) >= 100;
};

/**
 * Evaluates if a portfolio has all country allocation data.
 *
 * @param portfolio Portfolio to be evaluated.
 * @returns `true` if the portfolio has all country allocation data; `false` otherwise.
 */
const portfolioHasAllCountryAllocationData = (portfolio: Portfolio) => Math.round(sumAllocations(portfolio, 'allocations.country')) >= 100;

/**
 * Evaluates if a portfolio has all maturity allocation data.
 *
 * @param portfolio Portfolio to be evaluated.
 * @returns `true` if the portfolio has all maturity allocation data; `false` otherwise.
 */
const portfolioHasAllMaturityAllocationData = (portfolio: Portfolio) => Math.round(sumAllocations(portfolio, 'allocations.maturity')) >= 100;

/**
 * Evaluates if a portfolio has all credit quality allocation data.
 *
 * @param portfolio Portfolio to be evaluated.
 * @returns `true` if the portfolio has all credit quality allocation data; `false` otherwise.
 */
const portfolioHasAllCreditQualityAllocationData = (portfolio: Portfolio) => Math.round(sumAllocations(portfolio, 'allocations.creditQuality')) >= 100;

/**
 * Evaluates if a portfolio has all fixed income regions allocation data.
 *
 * @param portfolio Portfolio to be evaluated.
 * @returns `true` if the portfolio has all fixed income regions allocation data; `false` otherwise.
 */
const portfolioHasAllFixedIncomeRegionsAllocationData = (portfolio: Portfolio) => Math.round(sumAllocations(portfolio, 'allocations.fixedIncomeRegion')) >= 100;

/**
 * Evaluates if a portfolio has all fixed income sectors allocation data.
 *
 * @param portfolio Portfolio to be evaluated.
 * @returns `true` if the portfolio has all fixed income sectors allocation data; `false` otherwise.
 */
const portfolioHasAllFixedIncomeSectorsAllocationData = (portfolio: Portfolio) => Math.round(sumAllocations(portfolio, 'allocations.fixedIncomeSector')) >= 100;

/**
 * Evaluates if a portfolio has all fixed income style data.
 *
 * @param fund Portfolio to be evaluated.
 * @returns `true` if the portfolio has all fixed income style data; `false` otherwise.
 */
const portfolioHasAllFixedIncomeStyleData = (portfolio: Portfolio) => {
  const durationClass = Math.round(sumAllocations(portfolio, 'investmentInfo.durationClass'));
  const creditClass = Math.round(sumAllocations(portfolio, 'investmentInfo.creditRatingClass'));

  return durationClass >= 100 && creditClass >= 100;
};

/**
 * Evaluates if a portfolio has all equity style data.
 *
 * @param fund Portfolio to be evaluated.
 * @returns `true` if the portfolio has all equity style data; `false` otherwise.
 */
const portfolioHasAllEquityStyleData = (portfolio: Portfolio) => {
  const capitalization = Math.round(sumAllocations(portfolio, 'investmentInfo.capitalization'));
  const style = Math.round(sumAllocations(portfolio, 'investmentInfo.style'));

  return capitalization >= 100 && style >= 100;
};

/**
 * Gets the holdings inside a portfolio for which there is not enough benchmark data required for minimal calculations.
 *
 * @param portfolio Portfolio to be evaluated.
 * @param funds Funds data associated with the holdings.
 * @param benchmarks Benchmarks data associated with the funds.
 *
 * @returns An array of holdings for which the benchmarks data is incomplete.
 */
const getPortfolioHoldingsMissingBenchmarks = (portfolio: Portfolio, funds: Fund[], benchmarks: Benchmark[]) => {
  const [fundsHoldings] = splitPortfolioHoldings(portfolio);

  return fundsHoldings.filter(holding => {
    const fund = funds.find(f => f._id === holding.original.id);

    return !fund || !fundHasAllBenchmarkData(fund, benchmarks);
  });
};

const getPorfolioInseptionMoment = (portfolio: Portfolio, data: PortfolioData, useLongHistory?: boolean) => {
  const monthly = (portfolio.override?.rolling as Nullable<RollingPerformance>)?.monthly;
  const hasForcedRollings = (monthly || []).length > 0;
  if (monthly && hasForcedRollings) {
    return moment(monthly[0][0]);
  }

  const holdings = portfolio.holdings.reduce((acc, h) => {
    const datum = getPortfolioHoldingData(h, data);
    if (datum && datum.performance.rolling.monthly.length > 0) {
      acc.push(datum.performance.rolling.monthly[0][0]);
    }

    return acc;
  }, [] as number[]);

  if (holdings.length === 0) {
    return null;
  }

  holdings.sort();

  if (useLongHistory) {
    return moment(holdings[0]);
  }
  return moment(holdings[holdings.length - 1]);
};

const getPortfolioHoldingsMissingDataAtDate = (portfolio: Portfolio, data: PortfolioData, date: moment.Moment) => {
  const hasForcedRollings = ((portfolio.override?.rolling as Nullable<RollingPerformance>)?.monthly || []).length > 0;

  if (hasForcedRollings) {
    // If portfolio has forced rollings, don't even bother looking at the underlying holdings.
    return [];
  }

  return portfolio.holdings.reduce((acc, h) => {
    const datum = getPortfolioHoldingData(h, data);

    if (!datum || !datum.performance.rolling.monthly.length) {
      return [
        ...acc,
        {
          name: formatLocalizedString(h.name),
          symbol: h.symbol,
          date: new Date(),
        },
      ];
    }

    const monthlyRolling = datum.performance.rolling.monthly;

    if (moment(monthlyRolling[0][0]).isAfter(moment.utc(date))) {
      return [
        ...acc,
        {
          name: formatLocalizedString(h.name),
          symbol: h.symbol,
          date: moment(monthlyRolling[0][0]).toDate(),
        },
      ];
    }

    return acc;
  }, [] as ShortHistoryDisclaimerHolding[]);
};

const getPortfolioHoldingsMissingNavAtDate = (portfolio: Portfolio, data: PortfolioData, date: moment.Moment) =>
  portfolio.holdings.reduce((acc, h) => {
    const datum = getPortfolioHoldingData(h, data);

    if (!datum || !(datum as Stock | Fund).nav || !(datum as Stock | Fund).nav.monthly.length) {
      return [
        ...acc,
        {
          name: formatLocalizedString(h.name),
          symbol: h.symbol,
          date: new Date(),
        },
      ];
    }

    const monthlyNav = (datum as Stock | Fund).nav.monthly;

    if (moment(monthlyNav[0][0]).isAfter(moment.utc(date))) {
      return [
        ...acc,
        {
          name: formatLocalizedString(h.name),
          symbol: h.symbol,
          date: moment(monthlyNav[0][0]).toDate(),
        },
      ];
    }

    return acc;
  }, [] as ShortHistoryDisclaimerHolding[]);

const getPortfolioAltDisclaimers = (portfolio: Portfolio, data: PortfolioData) => {
  let disclaimers = portfolio.holdings.reduce((acc, h) => {
    if (h.original.type !== HOLDING_TYPES.ALT) {
      return acc;
    }

    const alt = getPortfolioHoldingData(h, data) as Alt | undefined;

    if (alt && alt.disclaimers) {
      alt.disclaimers.forEach(disclaimer => {
        const disclaimerData = disclaimer.content && Object.values(disclaimer.content).some(value => value !== '');
        if (!disclaimerData) return [];
        acc.push({
          name: formatLocalizedString(alt?.names?.full),
          ...disclaimer,
        });
      });
    }

    return acc;
  }, [] as CustomDisclaimerHolding[]);

  if (portfolio.disclaimers != null) {
    disclaimers = disclaimers.concat(
      portfolio.disclaimers.map(disclaimer => ({
        name: formatLocalizedString(portfolio.name),
        ...disclaimer,
      })),
    );
  }

  return disclaimers;
};

const getPortfolioCustomFundDisclaimers = (portfolio: Portfolio, data: PortfolioData) => {
  let disclaimers = portfolio.holdings.reduce((acc, h) => {
    if (h.original.type !== HOLDING_TYPES.FUND) {
      return acc;
    }

    const datum = getPortfolioHoldingData(h, data) as Fund | undefined;

    if (datum && datum.disclaimers) {
      datum.disclaimers.forEach(disclaimer => {
        const disclaimerData = disclaimer.content && Object.values(disclaimer.content).some(value => value !== '');
        if (!disclaimerData) {
          return [];
        }
        acc.push({
          name: formatLocalizedString(datum.names?.full),
          symbol: datum.symbols && datum.symbols[0],
          ...disclaimer,
        });
      });
    }

    return acc;
  }, [] as CustomDisclaimerHolding[]);

  if (portfolio.disclaimers != null) {
    disclaimers = disclaimers.concat(
      portfolio.disclaimers.map(disclaimer => ({
        name: formatLocalizedString(portfolio.name),
        ...disclaimer,
      })),
    );
  }

  return disclaimers;
};

const getPortfolioHoldingMissingNavDisclaimers = (portfolio: Portfolio, data: PortfolioData, locale?: Locale) =>
  portfolio.holdings.reduce((acc, h) => {
    const datum = getPortfolioHoldingData(h, data) as Fund | Stock | undefined;
    if (datum && datum.nav) {
      const isClosed = holdingNavIsClosed(datum);
      if (!isClosed) {
        return acc;
      }

      acc.push(
        formatMessageWithLocale('Comparison.ClosedNavTooltip.After', locale, {
          symbol: h.symbol || '',
          date: moment(datum.nav.monthly[datum.nav.monthly.length - 1][0]).format(formatMessageWithLocale('Common.DateFormats.Short', locale)),
        }),
      );
    }

    return acc;
  }, [] as string[]);

const getBenchmarkItemDisclaimers = (item: ComparisonItem, data: ComparisonBenchmark[]) =>
  item.holdings.reduce((acc, h) => {
    const datum = data.find(dataItem => dataItem._id === h.id);

    if (datum && datum.providerInfo) {
      acc.push({
        name: formatLocalizedString(h.name),
        providerName: datum.providerInfo.name,
        url: datum.providerInfo.url,
        id: h.id,
        color: item.color,
        key: datum.providerInfo.key,
      });
    }

    return acc;
  }, [] as BenchmarkProviderData[]);

const getComparisonItemHoldingsMissingDataAtDate = (item: ComparisonItem, date: moment.Moment) =>
  item.holdings.reduce((acc, h) => {
    if (!h.rolling || !h.rolling.monthly.length) {
      return [
        ...acc,
        {
          name: h.name ? formatLocalizedString(h.name) : undefined,
          symbol: h.symbol,
          date: new Date(),
        },
      ];
    }

    if (moment(h.rolling.monthly[0][0]).isSameOrAfter(moment.utc(date).endOf('month'))) {
      return [
        ...acc,
        {
          name: h.name ? formatLocalizedString(h.name) : undefined,
          symbol: h.symbol,
          date: moment(h.rolling.monthly[0][0]).toDate(),
        },
      ];
    }

    return acc;
  }, [] as ShortHistoryDisclaimerHolding[]);

const validatePortfolioTimePeriod = (
  portfolio: Portfolio,
  data: PortfolioData,
  period: TIME_PERIODS,
  useLongHistory?: boolean,
  commonInception?: CommonInception,
) => {
  let date = getDateFromTimePeriod(period);

  if (date == null) {
    if (period === TIME_PERIODS.CI && commonInception != null) {
      date = moment(commonInception.startTimestamp);
    } else if (period === TIME_PERIODS.INC) {
      date = getPorfolioInseptionMoment(portfolio, data, useLongHistory);
    }
  }

  if (!date) {
    return [];
  }

  return getPortfolioHoldingsMissingDataAtDate(portfolio, data, date);
};

const validateIncomeStreamNavTimePeriod = (portfolio: Portfolio, data: PortfolioData, year: number) => {
  const date = moment().year(year).startOf('year');

  return getPortfolioHoldingsMissingNavAtDate(portfolio, data, date);
};

const validatePortfolioCalendarYearPeriod = (portfolio: Portfolio, data: PortfolioData, year: number) => {
  const date = moment().year(year).startOf('year');

  return getPortfolioHoldingsMissingDataAtDate(portfolio, data, date);
};

const validateComparisonItemTimePeriod = (item: ComparisonItem, period: TimePeriods) => {
  const date = getDateFromTimePeriod(period);

  if (!date) {
    return [];
  }

  return getComparisonItemHoldingsMissingDataAtDate(item, date);
};

const validateComparisonItemCalendarYearPeriod = (item: ComparisonItem, year: number) => {
  const date = moment().year(year).endOf('year');

  return getComparisonItemHoldingsMissingDataAtDate(item, date);
};

const getPortfolioShortHistoryDisclaimers = (
  portfolio: Portfolio,
  data: PortfolioData,
  validateIncomeStream?: boolean,
  useLongHistory?: boolean,
  commonInception?: CommonInception,
) => {
  const shortDisclaimers: ShortHistoryDisclaimer[] = [];

  for (const period of Object.values(TIME_PERIODS)) {
    const missingHoldings = validatePortfolioTimePeriod(portfolio, data, period, useLongHistory, commonInception);

    if (missingHoldings.length) {
      shortDisclaimers.push({
        field: `performance_percentGrowth_${period}`,
        holdings: missingHoldings,
      });
    }
  }

  for (const year of getLastNYears(10)) {
    const missingHoldings = validatePortfolioCalendarYearPeriod(portfolio, data, year);

    if (missingHoldings.length) {
      shortDisclaimers.push({
        field: `performance_calendarYear_${year}`,
        holdings: missingHoldings,
      });
    }
  }

  if (validateIncomeStream) {
    for (const year of getLastNYears(10)) {
      const missingNavHoldings = validateIncomeStreamNavTimePeriod(portfolio, data, year);

      if (missingNavHoldings.length) {
        shortDisclaimers.push({
          field: `nav_${year}`,
          holdings: missingNavHoldings,
        });
      }
    }
  }

  return shortDisclaimers;
};

const getComparisonBenchmarkShortHistoryDisclaimers = (item: ComparisonItem) => {
  const shortDisclaimers: ShortHistoryDisclaimer[] = [];

  for (const period of Object.values(TIME_PERIODS)) {
    const missingHoldings = validateComparisonItemTimePeriod(item, period);

    if (missingHoldings.length) {
      shortDisclaimers.push({
        field: `performance_percentGrowth_${period}`,
        holdings: missingHoldings,
      });
    }
  }

  for (const year of getLastNYears(10)) {
    const missingHoldings = validateComparisonItemCalendarYearPeriod(item, year);

    if (missingHoldings.length) {
      shortDisclaimers.push({
        field: `performance_calendarYear_${year}`,
        holdings: missingHoldings,
      });
    }
  }

  return shortDisclaimers;
};

export default {
  formatPortfolioHoldingsList,
  formatComparisonItemHoldingsList,
  getAltDisclaimers,
  getFundDisclaimers,
  getFirstDataPointDate,
  getLastDataPointDate,
  getLastNavDataPointDate,
  holdingIsMissing,
  holdingIsClosed,
  holdingNavIsClosed,
  holdingHasNoData,
  holdingHasNoNavData,
  holdingHasLessThan3YData,
  holdingHasLessThan1YData,
  holdingIsGIC,
  holdingIsHISA,
  portfolioHasMissingOrInvalidHoldings,
  holdingIsMissingOrInvalid,
  fundHasAllBenchmarkData,
  fundHasAllTopHoldingsData,
  fundHasAllCountryAllocationData,
  fundHasAllIndustryHoldingsData,
  fundHasAllAssetDetailsData,
  fundHasAllEquityStyleData,
  fundHasAllFixedIncomeStyleData,
  fundHasSameBenchmark,
  fundHasCreditSecurities,
  fundHasEquity,
  getTrimmedHoldingName,
  portfolioHasNoMissingHolding,
  portfolioHasAllTopHoldingsData,
  portfolioHasAllCountryAllocationData,
  portfolioHasAllIndustryHoldingsData,
  portfolioHasAllAssetDetailsData,
  getPortfolioHoldingsMissingBenchmarks,
  portfolioHasAllMaturityAllocationData,
  portfolioHasAllCreditQualityAllocationData,
  portfolioHasAllFixedIncomeRegionsAllocationData,
  portfolioHasAllFixedIncomeSectorsAllocationData,
  portfolioHasAllFixedIncomeStyleData,
  portfolioHasAllEquityStyleData,
  portfolioHasCreditSecurities,
  portfolioHasEquity,
  fundHasAllMaturityAllocationData,
  fundHasAllCreditQualityAllocationData,
  fundHasAllFixedIncomeRegionsAllocationData,
  fundHasAllFixedIncomeSectorAllocationData,
  getPortfolioShortHistoryDisclaimers,
  getComparisonBenchmarkShortHistoryDisclaimers,
  getPortfolioAltDisclaimers,
  getPortfolioCustomFundDisclaimers,
  getBenchmarkItemDisclaimers,
  getPortfolioHoldingMissingNavDisclaimers,
};
